import React, { useState } from 'react';

interface Section {
  id: string;
  title: string;
  content: string | string[];
  subsections?: { title: string; content: string | string[] }[];
}

const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('introduction');

  const sections: Section[] = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: [
        'Gentari Sdn Bhd and its group of companies ("Gentari" or "We") are committed to protecting and respecting your privacy.',
        'Reference to "Gentari" herein refers to Gentari Sdn Bhd and its subsidiaries, controlling and controlled companies. The expression "Gentari" is used for convenience where references are made to the Gentari group of companies in general. The companies in which Gentari has direct or indirect shareholding are distinct legal entities.',
        'This may also include reference to joint venture companies in which Gentari is a non-controlling co-venturer and associated companies (as and where applicable).',
        'This Employee Privacy Notice ("Privacy Notice") explains what personal data we collect about our employees, directors, officers, workers, or other members of staff or personnel ("employees"), when and why we collect it, how we use it, the conditions under which we may disclose it to others, and how we keep it secure. Please read this notice carefully, together with any privacy notice that is provided on specific occasion when we are collecting or processing personal information about you, to understand our views and practices regarding your personal data and how we will treat it.'
      ]
    },
    {
      id: 'collection',
      title: 'Collection of Personal Data',
      content: [
        'Gentari may collect, store, use, disclose and otherwise process certain individually identifiable data about you as an employee ("Personal Data") to the extent permitted by applicable law, as set out in Schedule 1.',
        'Gentari may, under certain circumstances and only where strictly required by internal policies and procedures or applicable law, collect, store, use, disclose and otherwise process information relating to beneficial ownership in assets (where there is any asset declaration requirements applicable to you), and your political affiliations (where you intend to take up a political position or office).',
        'Gentari collects Personal Data directly from you or both internally or externally from a number of sources, including public records, former employers, referees, recruitment agencies, educational institutions, external consultants and such other permissible or authorized sources.',
        'Gentari may also collect, store, use, disclose and otherwise process certain information regarding your spouse(s), family members or other dependents for emergency contact and benefits administration purposes. Where you provide Personal Data about your spouse(s), family members or other dependents, it is your responsibility to inform such individuals about the processing of their Personal Data by Gentari for the purposes and in the manner described in this Privacy Notice.',
        'In respect of minors or individuals not legally competent to give consent, you confirm that they have appointed and/or authorized you to act for them and to consent on their behalf to the processing (including disclosure and transfer) of their Personal Data in accordance with this Privacy Notice.',
        'Gentari will not collect, use, store, disclose or otherwise process any Personal Data that is prohibited by applicable law. The type of Personal Data collected by Gentari may vary from jurisdiction to jurisdiction.',
        'Moreover, in addition to the Personal Data listed above, Gentari in accordance with applicable law, may also collect, use, store, disclose or otherwise process limited sensitive Personal Data that includes but is not limited to data set out in Schedule 1 ("Sensitive Personal Data").'
      ]
    },
    {
      id: 'purposes',
      title: 'Purposes of Processing of Personal Data',
      content: 'Gentari collects, stores, use, disclose and otherwise processes Personal Data for the following purposes:',
      subsections: [
        {
          title: 'Recruitment and Employment',
          content: 'For purposes of assessing and evaluating your suitability for employment or continued employment and fitness to work in any position in Gentari, including facilitating and supporting applications for work permits and/or visa.'
        },
        {
          title: 'HR and Organisational Planning',
          content: 'Operational and administrative matters, including granting access to our premises and group information technology systems, processing leave applications, organizational and workforce resource planning, conducting organizational surveys.'
        },
        {
          title: 'Talent Development and Assessment',
          content: 'Including for purposes of mobility (including secondment, transfer and assignment), progression (including promotions and upgrading), succession planning and performance evaluation, supporting training and development needs as well as talent assessments (e.g. skill matrix assessment, leadership assessment, talent review interviews).'
        },
        {
          title: 'Learning and Development',
          content: 'Certifications and Professional Membership, including supporting and managing registrations and logistical arrangements for training sessions, conferences, seminars and other events as well as certifications by statutory bodies, and to maintain and update certification and training inventories and records.'
        },
        {
          title: 'Remuneration and Benefits Administration',
          content: 'Including managing and administering your remuneration such as compensation, payroll, claims, bonuses, benefits, incentives, statutory contributions, deductions, administering your insurance and other benefits as well as other compensation-related matters.'
        },
        {
          title: 'Monitoring and Enforcement',
          content: 'Including monitoring your workplace attendance, your compliance with Gentari governing documents and applicable regulations, your use of PETRONAS or Gentari IT equipment for the purposes of enforcing compliance, ensuring network security and reliability and to promote workplace security and safety in general.'
        },
        {
          title: 'Exit Management',
          content: 'Including managing and facilitating exit procedures for resignations, terminations, retrenchments or Medical Boardout such as finalizing employment termination, conducting exit interviews, and handling the return of company property and settlement of any outstanding obligations.'
        },
        {
          title: 'Internal and External Reporting',
          content: 'To fulfil our reporting obligations to management as well as to statutory and regulatory bodies.'
        },
        {
          title: 'Data Analytics',
          content: 'Which informs our decision-making process and strategic focus areas.'
        },
        {
          title: 'Publications',
          content: 'Including publication and dissemination of your Personal Data in any circulars, reports, minutes, websites, newsletters, bulletins, brochures, pamphlets or any other materials which may be published and circulated internally or to the general public.'
        },
        {
          title: 'Business Continuity Planning',
          content: 'To ensure that essential functions and operations can continue during and after any disruptions or emergencies.'
        },
        {
          title: 'Records Management',
          content: 'Including maintaining and managing internal records to ensure accurate and organized documentation for administrative, legal, and operational purposes.'
        },
        {
          title: 'Audits and Assessments',
          content: 'Including conducting audit, assurance and risk assessments, whether internal or external.'
        },
        {
          title: 'Industrial Relations and Internal Investigations',
          content: 'Including managing and facilitating court or tribunal hearings, conducting internal investigations, overseeing disciplinary management, corruption and/or bribery investigations, and handling of any employment-related grievances.'
        },
        {
          title: 'Mergers and Acquisitions',
          content: 'Including facilitating any proposed mergers, acquisitions or business asset transaction involving any part of Gentari, or corporate restructuring process.'
        },
        {
          title: 'Legal Proceedings',
          content: 'Including preparing for and conducting legal proceedings and other similar activities, complying with court orders, and addressing any legal claims or disputes.'
        },
        {
          title: 'Regulatory and Contractual Compliance',
          content: 'Including facilitating our compliance with laws, customs and regulations which may be applicable to us, complying with law enforcement or government authority requests.'
        },
        {
          title: 'Diversity and Equal Opportunity Monitoring',
          content: 'Including diversity and other human resources related surveys, conduct related data analytics and reporting.'
        }
      ]
    },
    {
      id: 'disclosure',
      title: 'Disclosure of Personal Data',
      content: [
        'We may transfer your Personal Data to other companies within the Gentari group of companies, for any of the purposes stated under the Purposes of Processing section.',
        'Gentari may disclose your Personal Data to third party service providers in connection with human resources and payroll-related tasks (e.g., banks, insurance companies and other employee benefit providers) and to third party service providers in connection with information technology support and human resources support.',
        'Gentari and its group of companies may also share Personal Data with external advisors (e.g., lawyers, accountants and auditors) and with future employers in furtherance of your job application (upon your request and consent for us to do so) and/or any other third party with respect to potential acquisition and/or sale of any of Gentari\'s business or assets or other business combination.',
        'Gentari will exercise appropriate due diligence in the selection of such third party service providers, advisors, future employers and any third party purchasers, and require via appropriate contracts that such parties maintain adequate security measures to safeguard Personal Data.',
        'Gentari may also disclose Personal Data to governmental agencies and regulators, social organizations, human resources benefits providers, courts and other tribunals and government authorities, or to the extent required by applicable legal obligations or to comply with law enforcement or government authority requests.'
      ]
    },
    {
      id: 'security',
      title: 'Security Measures',
      content: [
        'We are committed to ensuring the security of your Personal Data. We have reasonable security measures in place to help protect against the loss, misuse and alteration of the information under our control.',
        'While we cannot guarantee that loss, misuse, or alteration of data will not occur, we ensure that our systems adhere to market security standards so as to help safeguard against such occurrences.',
        'You should also be aware, however, that no method of transmission over the internet or method of electronic storage is completely secure. While security cannot be guaranteed, we strive to protect the security of your Personal Data and will review and enhance our information security measures as and when necessary.',
        'Nevertheless, you are to ensure the security of your password and not to disclose the same to another party to prevent any data breach.'
      ]
    },
    {
      id: 'cross-border',
      title: 'Cross-Border Transfers of Personal Data',
      content: [
        'Gentari may from time to time transfer Personal Data to any other company within the Gentari group of companies, or to such other affiliated companies for the purposes as described above, whether within or outside the jurisdiction where Gentari is located.',
        'Where we transfer your Personal Data outside of Malaysia, we will take steps to ensure that the Personal Data is transferred to a country that has substantially similar laws to the Malaysia Personal Data Protection Act 2010 ("PDPA"), or to a country that has an adequate level of protection equivalent to that provided by the PDPA.',
        'Where such transfers occur, Gentari shall ensure that the relevant Gentari group of companies, or such other affiliated companies to whom personal data is transferred, shall provide comparable level of protection as we provide with respect to the Personal Data that we collect, process, store and disclose.'
      ]
    },
    {
      id: 'eea-transfers',
      title: 'Transfers out of the EEA',
      content: [
        'Any transfer of Personal Data of an employee of a Gentari entity within the European Economic Area ("EEA") to a country outside of the EEA, that has not been recognised by the European Union ("EU") Commission as providing an adequate level of data protection within the meaning of the General Data Protection Regulation ("GDPR") will be made subject to "appropriate safeguards" being in place to ensure the protection of your Personal Data.',
        'Appropriate safeguards include: legal agreements between public authorities or bodies; binding corporate rules; a code of conduct approved by a supervisory authority; a certification mechanism approved by a supervisory authority; or data transfer agreements based on the EU Commission standard contractual clauses for the transfer of data to third countries.',
        'In the absence of an adequacy decision and where appropriate safeguards are unavailable, transfers may take place on the basis of your consent. In such a situation, we will inform you of the possible risks associated with such a transfer.',
        'Alternatively, we may transfer your personal data where necessary: in connection with the performance of your employment contract; for reasons of public interest, or in connection with a legal claim.'
      ]
    },
    {
      id: 'employee-obligations',
      title: 'Employee\'s Obligations in Relation to Personal Data',
      content: [
        'Gentari will generally rely on Personal Data provided by you (or your authorized representative). In order to ensure that your Personal Data is current, complete and accurate, please ensure that the Personal Data furnished to us are kept up-to-date and accurate.',
        'You are able to update your information via Emplx or any related Human Resource Information System.'
      ]
    },
    {
      id: 'employee-rights',
      title: 'Employee\'s Rights in Relation to Personal Data',
      content: [
        'Gentari will maintain Personal Data for as long as Gentari is required to do so by applicable law(s), or for as long as necessary for the purpose(s) for which it was collected. Gentari will delete Personal Data when it is no longer needed in accordance with applicable law.',
        'Where you have given your consent to the processing of your Personal Data, you may withdraw this consent at any time. Withdrawal of your consent will not affect the lawfulness of any processing of your Personal Data that took place before you withdrew consent. Withdrawal of consent may however impact your ability to remain employed or otherwise engaged.',
        'Where entitled under the applicable laws, you are entitled to obtain access to or to request the rectification or deletion of Personal Data that Gentari holds about you. You may also object, on grounds relating to your particular situation, to the collection and processing of your Personal Data, and, in certain circumstances, you may request that Gentari restricts the processing of your Personal Data.',
        'If you would like to request access to, modification or deletion of, ask any questions about, object to, or request a restriction of the processing of your Personal Data, or discuss a transfer of your Personal Data, please contact us at the contact details provided below.'
      ]
    },
    {
      id: 'retention',
      title: 'Retention of Personal Data',
      content: [
        'Gentari may retain your Personal Data for as long as it is necessary to fulfil the purposes for which they were collected, or as required or permitted by applicable laws.',
        'We will cease to retain your Personal Data, or remove the means by which the Personal Data can be associated with you, as soon as it is reasonable to assume that such retention no longer serves the purposes for which the Personal Data were collected, and are no longer necessary for legal or business purposes.'
      ]
    },
    {
      id: 'changes',
      title: 'Changes to this Privacy Notice',
      content: [
        'We reserve the right to update and amend this Privacy Notice from time to time. While we will notify you of any substantial or impactful amendments to this Privacy Notice, we encourage you to regularly check the Gentari portal for the latest version of this Privacy Notice.',
        'You may determine if any such revision has taken place by referring to the date on which this Privacy Notice was last updated.'
      ]
    },
    {
      id: 'language',
      title: 'Language',
      content: 'In accordance with the requirement of local data protection and privacy law, this Privacy Notice is issued in both English and Bahasa Malaysia. In the event of any inconsistencies or discrepancies between the English version and the Bahasa Malaysia version, the English version shall prevail.'
    },
    {
      id: 'contact',
      title: 'How You Can Contact Us',
      content: [
        'Any questions, comments and requests regarding this Privacy Notice are welcomed and should be addressed to:',
        '',
        'Gentari Sdn. Bhd.',
        'Level 32, Permata Sapura. Kuala Lumpur City Centre,',
        '50088 Kuala Lumpur.',
        '',
        'Attn: Arien Zackary Ritzal, Chief Talent Officer',
        'Email: people@gentari.com'
      ]
    }
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-primary-600 p-3 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">EPM Privacy Policy</h1>
              <p className="text-gray-600 mt-1">Employee Privacy Notice</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
              Gentari Sdn. Bhd.
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Last updated: 1st August 2025
            </span>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Minimal Table of Contents */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Contents</h2>
                
                <nav className="space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-primary-600 text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-semibold ${
                          activeSection === section.id 
                            ? 'bg-white/20 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="flex-1 leading-tight">{section.title}</span>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Minimal Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-24 bg-white rounded-lg border border-gray-200 p-8"
              >
                {/* Section Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 bg-primary-600 text-white rounded-lg w-10 h-10 flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  {Array.isArray(section.content) ? (
                    section.content.map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))
                  ) : (
                    <p className="font-medium">{section.content}</p>
                  )}

                  {section.subsections && (
                    <div className="mt-6 space-y-4">
                      {section.subsections.map((subsection, subIdx) => (
                        <div key={subIdx} className="pl-6 border-l-2 border-primary-600">
                          <h3 className="font-semibold text-gray-900 mb-2">
                            {subsection.title}
                          </h3>
                          {Array.isArray(subsection.content) ? (
                            <div className="space-y-2 text-gray-700">
                              {subsection.content.map((item, itemIdx) => (
                                <p key={itemIdx}>{item}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-700">{subsection.content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ))}

            {/* Important Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">Important Notice</h3>
                  <p className="text-amber-800 text-sm leading-relaxed mb-3">
                    This Privacy Notice applies to current and former employees of Gentari.
                  </p>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    By using the Employee Performance Management system, you acknowledge that you have read, understood, and agree to the terms outlined in this Privacy Notice.
                  </p>
                </div>
              </div>
            </div>

            {/* Acknowledgment Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Acknowledgment and Consent</h3>
              
              <p className="text-gray-700 text-sm mb-4">
                Please check the following to signify your understanding and consent (without which we will not be able to process your Personal Data):
              </p>
              
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-5 h-5 rounded bg-primary-600 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-800 text-sm leading-relaxed">
                    I acknowledge that I have read and understood this statement and the purposes for which Gentari will process Personal Data (including Sensitive Personal Data). I expressly consent to Gentari processing my Personal Data (including my Sensitive Personal Data) in accordance with this Privacy Notice.
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Your continued use of the EPM system constitutes your acceptance of this Privacy Notice. If you have any questions, please contact us using the details in Section 13.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-600">
            <p>© 2025 Gentari. All rights reserved.</p>
            <p>Last reviewed: 1st August 2025</p>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;